import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Drawer, Field, Select, todayIso } from '@/components/live';
import { createRow, listRowsIn, removeRow, saveRow } from '@/api/resources';
import { FSC_CLAIMS } from '@/lib/practiceLists';
import { PLOT_SCOPES, scopeLabel, type ScopeColumn } from '@/lib/plotScopes';
import { toast } from '@/lib/toast';
import type { PlotRow } from '@/types/records';

interface ScopeRow { id: string; plot_id: string; scope_code: string }

/** The plot's own FSC FM columns, set for FSC FM instead of a scope row's. */
type FscField = 'cb_approved_on' | 'cb_approval_ref' | 'fsc_claim';

/**
 * One scope set, updated or removed on many plots at once, with a confirmation that says exactly
 * what will change (owner, 2026-09-27: "bulk update of the scope fields from datagrid table on
 * selection of multiple row with confirmation?").
 *
 * A field left blank is left as it is on each plot, so filling in one date does not wipe the
 * references already recorded. A plot gaining the scope needs the scope's required fields, as the
 * single-plot form does.
 */
export function PlotScopeBulk({ plots, open, onClose, onDone }: {
  plots: PlotRow[];
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [scopeCode, setScopeCode] = useState('');
  const [action, setAction] = useState<'set' | 'remove'>('set');
  const [values, setValues] = useState<Partial<Record<ScopeColumn, string>>>({});
  const [fsc, setFsc] = useState<Partial<Record<FscField, string>>>({});
  const [existing, setExisting] = useState<ScopeRow[] | null>(null);
  const [step, setStep] = useState<'edit' | 'confirm'>('edit');
  const [busy, setBusy] = useState(false);
  const scope = PLOT_SCOPES.find((entry) => entry.code === scopeCode);
  const ids = plots.map((plot) => plot.id);

  useEffect(() => {
    if (!open) return;
    setScopeCode(''); setAction('set'); setValues({}); setFsc({}); setStep('edit'); setExisting(null);
  }, [open]);

  const have = new Set((existing ?? []).filter((row) => row.scope_code === scopeCode).map((row) => row.plot_id));
  const gaining = plots.filter((plot) => !have.has(plot.id));
  const keeping = plots.filter((plot) => have.has(plot.id));
  const filled = scope?.usesPlotFields
    ? Object.entries(fsc).filter(([, value]) => value?.trim())
    : Object.entries(values).filter(([, value]) => value?.trim());

  async function review() {
    if (!scope) { toast.error('Choose the scope to change.'); return; }
    const problems: string[] = [];
    if (action === 'set') {
      if (scope.usesPlotFields) {
        if (fsc.fsc_claim === 'fsc_100' && !fsc.cb_approved_on) problems.push('An FSC 100% claim needs the date the certification body approved the plots.');
      } else {
        for (const field of scope.fields) {
          const value = values[field.column]?.trim();
          if (field.column === 'approved_on' && value && value > todayIso()) problems.push(`${field.label} cannot be in the future.`);
        }
        if (values.approved_on && values.valid_to && values.valid_to < values.approved_on) problems.push('It must end after it starts.');
      }
    }
    setBusy(true);
    try {
      const rows = await listRowsIn<ScopeRow>('plot_scopes', 'plot_id', ids);
      setExisting(rows);
      const already = new Set(rows.filter((row) => row.scope_code === scope.code).map((row) => row.plot_id));
      if (action === 'set' && !scope.usesPlotFields && plots.some((plot) => !already.has(plot.id))) {
        for (const field of scope.fields.filter((entry) => entry.required)) {
          if (!values[field.column]?.trim()) problems.push(`${field.label} is needed for the plots gaining ${scope.label}.`);
        }
      }
      if (action === 'remove' && already.size === 0) problems.push(`None of these plots is in ${scope.label}.`);
      if (problems.length) { toast.errors('Before applying', problems); return; }
      setStep('confirm');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The plots\' scopes could not be read.');
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    if (!scope || !existing) return;
    setBusy(true);
    const failed: string[] = [];
    const rowOf = (plotId: string) => existing.find((row) => row.plot_id === plotId && row.scope_code === scope.code);
    const given = Object.fromEntries(filled.map(([key, value]) => [key, value!.trim()]));
    for (const plot of plots) {
      try {
        const row = rowOf(plot.id);
        if (action === 'remove') {
          if (!row) continue;
          await removeRow('plot_scopes', row.id, `${scope.label} removed from the plot in a bulk change.`);
          if (scope.usesPlotFields) await saveRow('farm_plots', plot.id, { cb_approved_on: null, cb_approval_ref: null, fsc_claim: 'none' });
        } else if (scope.usesPlotFields) {
          if (!row) await createRow('plot_scopes', { plot_id: plot.id, scope_code: scope.code });
          if (Object.keys(given).length > 0) await saveRow('farm_plots', plot.id, given);
        } else if (row) {
          if (Object.keys(given).length > 0) await saveRow('plot_scopes', row.id, given);
        } else {
          await createRow('plot_scopes', { plot_id: plot.id, scope_code: scope.code, ...given });
        }
      } catch (error) {
        failed.push(`${plot.plot_code}: ${error instanceof Error ? error.message : 'not saved'}`);
      }
    }
    await queryClient.invalidateQueries({ queryKey: ['plot_scopes'] });
    await queryClient.invalidateQueries({ queryKey: ['farm_plots'] });
    setBusy(false);
    const done = plots.length - failed.length;
    if (failed.length) toast.errors(`${done} of ${plots.length} plots changed`, failed);
    else toast.success(`${scope.label} ${action === 'remove' ? 'removed from' : 'set on'} ${done} plot${done === 1 ? '' : 's'}.`);
    onDone();
  }

  const list = (items: PlotRow[]) => items.slice(0, 12).map((plot) => plot.plot_code).join(', ') + (items.length > 12 ? ` and ${items.length - 12} more` : '');

  return (
    <Drawer
      footer={step === 'edit' ? (
        <>
          <button className="cancel" onClick={onClose} type="button">Cancel</button>
          <button className="primary" disabled={busy || !scope} onClick={() => void review()} type="button">
            {busy ? 'Checking...' : 'Review changes'}
          </button>
        </>
      ) : (
        <>
          <button onClick={() => setStep('edit')} type="button">Back</button>
          <button className={action === 'remove' ? 'primary danger' : 'primary'} disabled={busy} onClick={() => void apply()} type="button">
            {busy ? 'Applying...' : `Confirm - ${action === 'remove' ? 'remove from' : 'apply to'} ${action === 'remove' ? keeping.length : plots.length}`}
          </button>
        </>
      )}
      onClose={onClose}
      open={open}
      subtitle={`${plots.length} plot${plots.length === 1 ? '' : 's'} selected`}
      title={step === 'edit' ? 'Update scope' : 'Confirm the change'}
    >
      {step === 'edit' ? (
        <>
          <div className="field-row">
            <Field label="Scope" required>
              <Select onChange={(value) => { setScopeCode(value); setValues({}); setFsc({}); }}
                      options={PLOT_SCOPES.map((entry) => ({ id: entry.code, label: entry.label }))} placeholder="Select scope" value={scopeCode} />
            </Field>
            <Field label="Change">
              <div aria-label="Change" className="view-toggle" role="group">
                {([['set', 'Set / update'], ['remove', 'Remove']] as const).map(([value, label]) => (
                  <button aria-pressed={action === value} key={value} onClick={() => setAction(value)} type="button">{label}</button>
                ))}
              </div>
            </Field>
          </div>
          {scope && action === 'set' && (
            <>
              <p className="muted" style={{ margin: 0 }}>
                Plots not in {scope.label} are added to it. A field left blank stays as it is on each plot.
              </p>
              {scope.usesPlotFields ? (
                <>
                  <div className="field-row">
                    <Field label="Date of approval by CB as member">
                      <input max={todayIso()} onChange={(event) => setFsc({ ...fsc, cb_approved_on: event.target.value })} type="date" value={fsc.cb_approved_on ?? ''} />
                    </Field>
                    <Field label="CB approval reference">
                      <input onChange={(event) => setFsc({ ...fsc, cb_approval_ref: event.target.value })} value={fsc.cb_approval_ref ?? ''} />
                    </Field>
                  </div>
                  <Field label="FSC claim">
                    <Select onChange={(value) => setFsc({ ...fsc, fsc_claim: value })} options={FSC_CLAIMS} placeholder="Leave as it is" value={fsc.fsc_claim ?? ''} />
                  </Field>
                </>
              ) : (
                <div className="field-row">
                  {scope.fields.map((field) => {
                    const value = values[field.column] ?? '';
                    const set = (next: string) => setValues({ ...values, [field.column]: next });
                    return (
                      <Field hint={field.hint} key={field.column} label={field.label} required={field.required}>
                        {field.kind === 'choice'
                          ? <Select onChange={set} options={field.choices ?? []} placeholder="Leave as it is" value={value} />
                          : <input max={field.kind === 'date' && field.column === 'approved_on' ? todayIso() : undefined}
                                   onChange={(event) => set(event.target.value)} type={field.kind === 'date' ? 'date' : 'text'} value={value} />}
                      </Field>
                    );
                  })}
                </div>
              )}
            </>
          )}
          {scope && action === 'remove' && (
            <p className="muted" style={{ margin: 0 }}>
              {scope.label} and its details are taken off every selected plot that has it; each removal keeps its reason in the history.
              {scope.usesPlotFields ? ' The plots\' CB approval and FSC claim are cleared with it.' : ''}
            </p>
          )}
        </>
      ) : scope && (
        <div className="bulk-confirm">
          {action === 'remove' ? (
            <p><strong>{scope.label}</strong> will be removed from <strong>{keeping.length}</strong> plot{keeping.length === 1 ? '' : 's'}: {list(keeping)}.
              {gaining.length > 0 && <> {gaining.length} selected plot{gaining.length === 1 ? ' is' : 's are'} not in it and stay{gaining.length === 1 ? 's' : ''} as {gaining.length === 1 ? 'it is' : 'they are'}.</>}</p>
          ) : (
            <>
              {gaining.length > 0 && <p><strong>{scope.label}</strong> will be added to <strong>{gaining.length}</strong> plot{gaining.length === 1 ? '' : 's'}: {list(gaining)}.</p>}
              {keeping.length > 0 && <p><strong>{keeping.length}</strong> already in {scope.label} will be updated: {list(keeping)}.</p>}
              {filled.length > 0 ? (
                <ul>
                  {filled.map(([key, value]) => {
                    const label = scope.usesPlotFields
                      ? ({ cb_approved_on: 'Date of approval by CB', cb_approval_ref: 'CB approval reference', fsc_claim: 'FSC claim' } as Record<string, string>)[key]
                      : scope.fields.find((field) => field.column === key)?.label;
                    const shown = scope.usesPlotFields && key === 'fsc_claim'
                      ? FSC_CLAIMS.find((option) => option.id === value)?.label ?? value
                      : scope.fields.find((field) => field.column === key)?.choices?.find((choice) => choice.id === value)?.label ?? value;
                    return <li key={key}>{label}: <strong>{shown}</strong></li>;
                  })}
                </ul>
              ) : <p className="muted">No fields filled in: the plots are only marked as in {scopeLabel(scope.code)}.</p>}
            </>
          )}
        </div>
      )}
    </Drawer>
  );
}
